// Display copy for a server suggestion `reason`. The client owns this mapping; an
// unrecognized reason renders no subtext rather than leaking a raw enum value.
// Shared so the Issues and Projects label pickers cannot drift.
export const SUGGESTION_REASON_TEXT: Record<string, string> = {
  user_frequent: 'Recent',
  popular: 'Popular',
}

// Analytics attribution values for the `item-picker-label-suggestion-select`
// event. Suggestions report the server's own `reason` verbatim so client
// telemetry stays joinable with server-side ranking; these cover the two cases
// the client attributes itself. `searchResult` is the fallback for anything not
// surfaced as a suggestion (typed-then-selected, or overflow beyond the cap).
// Shared so the Issues and Projects label pickers report the same values into
// the same funnel.
export const SUGGESTION_REASON = {
  copilot: 'copilot',
  searchResult: 'search_result',
} as const

export const LABELS = {
  noAssignees: 'Assignee',
  assignees: 'Assignees',
  assigneesHeader: 'Select assignees',
  cantEditItems: (itemType: string) => `Cannot edit ${itemType} right now`,
  copilotDisplayName: 'Copilot',
  copilotDescription: 'Your AI pair programmer',
  noLabels: 'Label',
  labels: 'Labels',
  labelsHeader: 'Apply labels to this issue',
  editLabels: 'Edit labels',
  noProjects: 'Project',
  noMilestones: 'Milestone',
  milestonesHeader: 'Set milestone',
  issueHeader: 'Choose issue',
  noIssues: 'Issue',
  noIssueTypes: 'Issue type',
  filterOrganizations: 'Filter organizations',
  organizationLabel: 'Organization',
  selectAssignees: 'Select assignees',
  selectMilestones: 'Select milestone',
  selectIssue: 'Select issue',
  selectIssueTypes: 'Select issue type',
  typesHeader: 'Select issue type',
  fieldsHeader: 'Select issue field',
  fieldSingleSelectHeader: 'Select an option',
  fieldSingleSelectHeaderWithName: (fieldName: string) => `Select ${fieldName}`,
  fieldMultiSelectHeader: 'Select options',
  fieldMultiSelectHeaderWithName: (fieldName: string) => `Select ${fieldName}`,
  selectProjects: 'Select projects',
  selectLabels: 'Select labels',
  selectRepository: 'Select repository',
  selectBranch: 'Select a branch',
  selectPr: 'Select a pull request',
  searchPr: 'Search pull requests',
  maxPRsLinkingWarning: 'You may only link 10 pull requests to this issue across all repositories.',
  filterUsers: (title: string) => `Filter ${title.toLowerCase()}s`,
  filterMilestones: 'Filter milestones',
  filterIssueTypes: 'Filter issue types',
  filterProjects: 'Filter projects',
  noPullRequest: 'no pull request',
  milestones: {
    pastDue: 'Past due by',
    milestoneClosed: 'Closed',
    milestoneDue: 'Due by',
    noDueDate: 'No due date',
    progressPercentage: (progress: number) => (progress ? `, ${Math.round(progress)}% complete` : ', not started'),
  },
  testIds: {
    relativeTimeDescription: 'relative-time-description',
  },
  getNumberOfSelectedPrsLabel: (count: number) =>
    count === 1 ? `${count} pull request selected` : `${count} pull requests selected`,
  projectItemsLimitReached:
    'This project has reached the maximum of 50,000 items. To add more, please delete existing items from the project.',
  closeButton: 'Close dialog',
  projectItemsLimitReachedDialogLabel: 'Project items limit reached dialog',
  projectItemsLimitReachedDialogTitle: 'Project limits reached',
  projectItemsLimitReachedDialogMessage:
    'One or more of the projects you selected has reached the maximum of 50,000 items. To add more, please delete existing items from the selected project(s).',
  limitWarning: (count: number) =>
    count === 1
      ? `You have reached the limit of ${count} assignee`
      : `You have reached the limit of ${count} assignees`,
}
